import { ref, onValue, get, set, update, query, orderByChild, limitToLast } from 'firebase/database';
import { db } from '../lib/firebase';
import { User, LeaderboardType } from '../types';
import { startOfWeek, startOfMonth, isAfter } from 'date-fns';
import { processScoreUpdateForChallenges } from './challengeService';
import { notifyFollowers } from './notificationService';

const usersRef = ref(db, 'users');
const weeklyRef = ref(db, 'weekly');
const monthlyRef = ref(db, 'monthly');
const allTimeRef = ref(db, 'allTime');

// Memory game specific leaderboard refs
const memoryWeeklyRef = ref(db, 'memoryWeekly');
const memoryMonthlyRef = ref(db, 'memoryMonthly');
const memoryAllTimeRef = ref(db, 'memoryAllTime');

// Memory game RankingBoard tree
const memoryRankingBoardWeeklyRef = ref(db, 'RankingBoard/memory/weekly');
const memoryRankingBoardMonthlyRef = ref(db, 'RankingBoard/memory/monthly');
const memoryRankingBoardAllTimeRef = ref(db, 'RankingBoard/memory/allTime');

// News RankingBoard tree
const newsRankingBoardWeeklyRef = ref(db, 'RankingBoard/news/weekly');
const newsRankingBoardMonthlyRef = ref(db, 'RankingBoard/news/monthly');
const newsRankingBoardAllTimeRef = ref(db, 'RankingBoard/news/allTime');

// Fetch users and sort by appropriate score
export const getLeaderboard = (
  type: LeaderboardType,
  callback: (users: User[]) => void,
  limit = 10
) => {
  let targetRef;

  if (type === 'weekly') {
    targetRef = weeklyRef;
  } else if (type === 'monthly') {
    targetRef = monthlyRef;
  } else {
    targetRef = allTimeRef;
  }

  const leaderboardQuery = query(targetRef, orderByChild('score'), limitToLast(limit));

  return onValue(leaderboardQuery, (snapshot) => {
    const data = snapshot.val();
    if (!data) {
      callback([]);
      return;
    }

    // Convert object to array
    const users: User[] = Object.entries(data).map(([id, val]: [string, any]) => {
      return {
        id,
        username: val.username,
        // Since we are fetching from specific leaderboard nodes, 'score' is the relevant score for that period
        // We map it to the expected property based on type, or just fill all for safety if needed,
        // but 'score' in the specific node corresponds to that period's score.
        // The UI component selects the score based on 'type' prop.
        weeklyScore: type === 'weekly' ? Number(val.score || 0) : 0,
        monthlyScore: type === 'monthly' ? Number(val.score || 0) : 0,
        score: type === 'allTime' ? Number(val.score || 0) : 0,
        // profilePhotoUrl is sometimes present in these nodes (from seedInitialData) but not always updated
        // UserCard generates random avatar if missing, so it's fine.
        ...val
      } as unknown as User;
    });

    // Sort descending (limitToLast returns ascending)
    const sortedUsers = users.sort((a, b) => {
      if (type === 'weekly') {
        return (b.weeklyScore || 0) - (a.weeklyScore || 0);
      } else if (type === 'monthly') {
        return (b.monthlyScore || 0) - (a.monthlyScore || 0);
      } else {
        return (b.score || 0) - (a.score || 0);
      }
    });

    callback(sortedUsers);
  });
};

// Memory-game-only leaderboard
export const getMemoryLeaderboard = (
  type: LeaderboardType,
  callback: (users: User[]) => void,
  limit = 10
) => {
  let targetRef;

  if (type === 'weekly') {
    targetRef = memoryWeeklyRef;
  } else if (type === 'monthly') {
    targetRef = memoryMonthlyRef;
  } else {
    targetRef = memoryAllTimeRef;
  }

  const leaderboardQuery = query(targetRef, orderByChild('score'), limitToLast(limit));

  return onValue(leaderboardQuery, (snapshot) => {
    const data = snapshot.val();
    if (!data) {
      callback([]);
      return;
    }

    const users: User[] = Object.entries(data).map(([id, val]: [string, any]) => ({
      id,
      username: val.username,
      // Map to memory scores
      memoryWeeklyScore: type === 'weekly' ? Number(val.score || 0) : 0,
      memoryMonthlyScore: type === 'monthly' ? Number(val.score || 0) : 0,
      memoryScore: type === 'allTime' ? Number(val.score || 0) : 0,
      // Also map to standard scores if UI expects them fallback
      weeklyScore: type === 'weekly' ? Number(val.score || 0) : 0,
      monthlyScore: type === 'monthly' ? Number(val.score || 0) : 0,
      score: type === 'allTime' ? Number(val.score || 0) : 0,
      ...val
    })) as unknown as User[];

    const sortedUsers = users.sort((a: any, b: any) => {
      if (type === 'weekly') {
        return b.memoryWeeklyScore - a.memoryWeeklyScore;
      } else if (type === 'monthly') {
        return b.memoryMonthlyScore - a.memoryMonthlyScore;
      } else {
        return b.memoryScore - a.memoryScore || 0;
      }
    });

    callback(sortedUsers);
  });
};

// Update user scores
export const updateUserScores = async (userId: string, points: number, user: User) => {
  const userRef = ref(db, `users/${userId}`);
  const snapshot = await get(userRef);
  const userData = snapshot.val();

  if (!userData) return;

  const now = new Date();
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 1 });
  const startOfCurrentMonth = startOfMonth(now);

  let currentWeeklyScore = userData.weeklyScore || 0;
  let currentMonthlyScore = userData.monthlyScore || 0;
  const additionalUpdates: Record<string, any> = {};

  if (!userData.lastWeeklyReset || isAfter(startOfCurrentWeek, new Date(userData.lastWeeklyReset))) {
    currentWeeklyScore = 0;
    additionalUpdates.lastWeeklyReset = startOfCurrentWeek.getTime();
  }

  if (!userData.lastMonthlyReset || isAfter(startOfCurrentMonth, new Date(userData.lastMonthlyReset))) {
    currentMonthlyScore = 0;
    additionalUpdates.lastMonthlyReset = startOfCurrentMonth.getTime();
  }

  const updates = {
    score: (userData.score || 0) + points,
    weeklyScore: currentWeeklyScore + points,
    monthlyScore: currentMonthlyScore + points,
    ...additionalUpdates
  };

  // Update user scores
  await update(userRef, updates);

  if(!userData.username) {
    console.log('user dont have name');
    const userNameRef = ref(db, `users/${userId}/username`);
    console.log('user', user);
    set(userNameRef, user?.displayName);
  }

  // Update leaderboard nodes
  await Promise.all([
    update(ref(db, `weekly/${userId}`), { 
      score: updates.weeklyScore,
      username: userData.username,
    }),
    update(ref(db, `monthly/${userId}`), { 
      score: updates.monthlyScore,
      username: userData.username,
    }),
    update(ref(db, `allTime/${userId}`), { 
      score: updates.score,
      username: userData.username,
    })
  ]);

  // Hook into challenges and notifications
  await processScoreUpdateForChallenges(userId, updates.score);
  // Send notification about earning points
  if (points >= 10) {
    await notifyFollowers(userId, 'activity_completed', `${userData.username || 'A friend'} just earned ${points} points!`);
  }
};

// Update user scores for memory game only
export const updateUserMemoryScores = async (userId: string, points: number, user: User) => {
  const userRef = ref(db, `users/${userId}`);
  const snapshot = await get(userRef);
  const userData = snapshot.val();

  if (!userData) return;

  const now = new Date();
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 1 });
  const startOfCurrentMonth = startOfMonth(now);

  let currentWeeklyScore = userData.memoryWeeklyScore || 0;
  let currentMonthlyScore = userData.memoryMonthlyScore || 0;

  const updates: Record<string, any> = {};

  if (!userData.memoryLastWeeklyReset || isAfter(startOfCurrentWeek, new Date(userData.memoryLastWeeklyReset))) {
    currentWeeklyScore = 0;
    updates.memoryLastWeeklyReset = startOfCurrentWeek.getTime();
  }

  if (!userData.memoryLastMonthlyReset || isAfter(startOfCurrentMonth, new Date(userData.memoryLastMonthlyReset))) {
    currentMonthlyScore = 0;
    updates.memoryLastMonthlyReset = startOfCurrentMonth.getTime();
  }

  updates.memoryScore = (userData.memoryScore || 0) + points;
  updates.memoryWeeklyScore = currentWeeklyScore + points;
  updates.memoryMonthlyScore = currentMonthlyScore + points;

  await update(userRef, updates);

  // ensure username exists
  if (!userData.username) {
    const userNameRef = ref(db, `users/${userId}/username`);
    set(userNameRef, (user as any)?.displayName);
  }

  // Update memory leaderboard nodes
  await Promise.all([
    update(ref(db, `memoryWeekly/${userId}`), {
      score: updates.memoryWeeklyScore,
      username: userData.username,
    }),
    update(ref(db, `memoryMonthly/${userId}`), {
      score: updates.memoryMonthlyScore,
      username: userData.username,
    }),
    update(ref(db, `memoryAllTime/${userId}`), {
      score: updates.memoryScore,
      username: userData.username,
    }),
    // Mirror to RankingBoard/memory tree
    update(ref(db, `RankingBoard/memory/weekly/${userId}`), {
      score: updates.memoryWeeklyScore,
      username: userData.username,
      profilePhotoUrl: (userData as any).profilePhotoUrl || null,
    }),
    update(ref(db, `RankingBoard/memory/monthly/${userId}`), {
      score: updates.memoryMonthlyScore,
      username: userData.username,
      profilePhotoUrl: (userData as any).profilePhotoUrl || null,
    }),
    update(ref(db, `RankingBoard/memory/allTime/${userId}`), {
      score: updates.memoryScore,
      username: userData.username,
      profilePhotoUrl: (userData as any).profilePhotoUrl || null,
    }),
  ]);

  // Hook into challenges and notifications
  await processScoreUpdateForChallenges(userId, updates.memoryScore);
  if (points >= 10) {
    await notifyFollowers(userId, 'activity_completed', `${userData.username || 'A friend'} just earned ${points} points in Memory Game!`);
  }
};

// Update user scores for news only
export const updateUserNewsScores = async (userId: string, points: number, user: User) => {
  const userRef = ref(db, `users/${userId}`);
  const snapshot = await get(userRef);
  const userData = snapshot.val();

  if (!userData) return;

  const now = new Date();
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 1 });
  const startOfCurrentMonth = startOfMonth(now);

  let currentWeeklyScore = userData.newsWeeklyScore || 0;
  let currentMonthlyScore = userData.newsMonthlyScore || 0;

  const updates: Record<string, any> = {};

  if (!userData.newsLastWeeklyReset || isAfter(startOfCurrentWeek, new Date(userData.newsLastWeeklyReset))) {
    currentWeeklyScore = 0;
    updates.newsLastWeeklyReset = startOfCurrentWeek.getTime();
  }

  if (!userData.newsLastMonthlyReset || isAfter(startOfCurrentMonth, new Date(userData.newsLastMonthlyReset))) {
    currentMonthlyScore = 0;
    updates.newsLastMonthlyReset = startOfCurrentMonth.getTime();
  }

  updates.newsScore = (userData.newsScore || 0) + points;
  updates.newsWeeklyScore = currentWeeklyScore + points;
  updates.newsMonthlyScore = currentMonthlyScore + points;

  await update(userRef, updates);

  if (!userData.username) {
    const userNameRef = ref(db, `users/${userId}/username`);
    set(userNameRef, (user as any)?.displayName);
  }

  // Update News RankingBoard
  await Promise.all([
    update(ref(db, `RankingBoard/news/weekly/${userId}`), {
      score: updates.newsWeeklyScore,
      username: userData.username,
      profilePhotoUrl: (userData as any).profilePhotoUrl || null,
    }),
    update(ref(db, `RankingBoard/news/monthly/${userId}`), {
      score: updates.newsMonthlyScore,
      username: userData.username,
      profilePhotoUrl: (userData as any).profilePhotoUrl || null,
    }),
    update(ref(db, `RankingBoard/news/allTime/${userId}`), {
      score: updates.newsScore,
      username: userData.username,
      profilePhotoUrl: (userData as any).profilePhotoUrl || null,
    }),
  ]);

  await processScoreUpdateForChallenges(userId, updates.newsScore);
  if (points >= 10) {
    await notifyFollowers(userId, 'activity_completed', `${userData.username || 'A friend'} just earned ${points} points in Daily News!`);
  }
};

// Check if weekly or monthly scores need to be reset
export const checkAndResetScores = async () => {
  const snapshot = await get(usersRef);
  const data = snapshot.val();
  if (!data) return;

  const now = new Date();
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 1 }); // Monday
  const startOfCurrentMonth = startOfMonth(now);

  const updates: Record<string, any> = {};
  const weeklyUpdates: Record<string, any> = {};
  const monthlyUpdates: Record<string, any> = {};
  const rbWeeklyUpdates: Record<string, any> = {};
  const rbMonthlyUpdates: Record<string, any> = {};

  Object.entries(data).forEach(async ([userId, userData]) => {
    const user = userData as User;
    let needsUpdate = false;

    // Check if weekly score needs to be reset
    if (!user.lastWeeklyReset || isAfter(startOfCurrentWeek, new Date(user.lastWeeklyReset))) {
      updates[`${userId}/weeklyScore`] = 0;
      updates[`${userId}/lastWeeklyReset`] = startOfCurrentWeek.getTime();
      weeklyUpdates[userId] = null; // Remove from weekly leaderboard
      needsUpdate = true;
    }

    // Check if monthly score needs to be reset
    if (!user.lastMonthlyReset || isAfter(startOfCurrentMonth, new Date(user.lastMonthlyReset))) {
      updates[`${userId}/monthlyScore`] = 0;
      updates[`${userId}/lastMonthlyReset`] = startOfCurrentMonth.getTime();
      monthlyUpdates[userId] = null; // Remove from monthly leaderboard
      needsUpdate = true;
    }

    // Apply updates if needed
  if (Object.keys(updates).length > 0 && needsUpdate) {
    await Promise.all([
      update(usersRef, updates),
      update(weeklyRef, weeklyUpdates),
      update(monthlyRef, monthlyUpdates)
    ]);
  }
  });
};

// Reset Memory scores + clean leaderboards
export const checkAndResetMemoryScores = async () => {
  const snapshot = await get(usersRef);
  const data = snapshot.val();
  if (!data) return;

  const now = new Date();
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 1 });
  const startOfCurrentMonth = startOfMonth(now);

  const updates: Record<string, any> = {};

  // Memory leaderboards (your app’s leaderboard nodes)
  const weeklyUpdates: Record<string, any> = {};
  const monthlyUpdates: Record<string, any> = {};

  // RankingBoard leaderboards (separate RB nodes)
  const rbWeeklyUpdates: Record<string, any> = {};
  const rbMonthlyUpdates: Record<string, any> = {};

  for (const [userId, userData] of Object.entries<any>(data)) {
    if (
      !userData.memoryLastWeeklyReset ||
      isAfter(startOfCurrentWeek, new Date(userData.memoryLastWeeklyReset))
    ) {
      updates[`${userId}/memoryWeeklyScore`] = 0;
      updates[`${userId}/memoryLastWeeklyReset`] = startOfCurrentWeek.getTime();

      // remove from weekly leaderboards (passing null deletes the key in RTDB)
      weeklyUpdates[userId] = null;
      rbWeeklyUpdates[userId] = null;
    }

    if (
      !userData.memoryLastMonthlyReset ||
      isAfter(startOfCurrentMonth, new Date(userData.memoryLastMonthlyReset))
    ) {
      updates[`${userId}/memoryMonthlyScore`] = 0;
      updates[`${userId}/memoryLastMonthlyReset`] = startOfCurrentMonth.getTime();

      // remove from monthly leaderboards
      monthlyUpdates[userId] = null;
      rbMonthlyUpdates[userId] = null;
    }
  }

  const hasAny =
    Object.keys(updates).length ||
    Object.keys(weeklyUpdates).length ||
    Object.keys(monthlyUpdates).length ||
    Object.keys(rbWeeklyUpdates).length ||
    Object.keys(rbMonthlyUpdates).length;

  if (hasAny) {
    await Promise.all([
      Object.keys(updates).length
        ? update(usersRef, updates)
        : Promise.resolve(),
      Object.keys(weeklyUpdates).length
        ? update(memoryWeeklyRef, weeklyUpdates)
        : Promise.resolve(),
      Object.keys(monthlyUpdates).length
        ? update(memoryMonthlyRef, monthlyUpdates)
        : Promise.resolve(),
      Object.keys(rbWeeklyUpdates).length
        ? update(memoryRankingBoardWeeklyRef, rbWeeklyUpdates)
        : Promise.resolve(),
      Object.keys(rbMonthlyUpdates).length
        ? update(memoryRankingBoardMonthlyRef, rbMonthlyUpdates)
        : Promise.resolve(),
    ]);
  }
};

// Reset News scores
export const checkAndResetNewsScores = async () => {
  const snapshot = await get(usersRef);
  const data = snapshot.val();
  if (!data) return;

  const now = new Date();
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 1 });
  const startOfCurrentMonth = startOfMonth(now);

  const updates: Record<string, any> = {};
  const rbWeeklyUpdates: Record<string, any> = {};
  const rbMonthlyUpdates: Record<string, any> = {};

  for (const [userId, userData] of Object.entries<any>(data)) {
    if (!userData.newsLastWeeklyReset || isAfter(startOfCurrentWeek, new Date(userData.newsLastWeeklyReset))) {
      updates[`${userId}/newsWeeklyScore`] = 0;
      updates[`${userId}/newsLastWeeklyReset`] = startOfCurrentWeek.getTime();
      rbWeeklyUpdates[userId] = null;
    }
    if (!userData.newsLastMonthlyReset || isAfter(startOfCurrentMonth, new Date(userData.newsLastMonthlyReset))) {
      updates[`${userId}/newsMonthlyScore`] = 0;
      updates[`${userId}/newsLastMonthlyReset`] = startOfCurrentMonth.getTime();
      rbMonthlyUpdates[userId] = null;
    }
  }

  const hasAny = Object.keys(updates).length || Object.keys(rbWeeklyUpdates).length || Object.keys(rbMonthlyUpdates).length;

  if (hasAny) {
    await Promise.all([
      Object.keys(updates).length ? update(usersRef, updates) : Promise.resolve(),
      Object.keys(rbWeeklyUpdates).length ? update(newsRankingBoardWeeklyRef, rbWeeklyUpdates) : Promise.resolve(),
      Object.keys(rbMonthlyUpdates).length ? update(newsRankingBoardMonthlyRef, rbMonthlyUpdates) : Promise.resolve(),
    ]);
  }
};

// Read Memory RankingBoard
export const getMemoryRankingBoard = (
  type: LeaderboardType,
  callback: (users: User[]) => void,
  limit = 10
) => {
  const targetRef =
    type === 'weekly'
      ? memoryRankingBoardWeeklyRef
      : type === 'monthly'
      ? memoryRankingBoardMonthlyRef
      : memoryRankingBoardAllTimeRef;

  // Use query to fetch only top results
  const leaderboardQuery = query(targetRef, orderByChild('score'), limitToLast(limit));

  return onValue(leaderboardQuery, (snapshot) => {
    const data = snapshot.val() || {};
    const entries: Array<{
      id: string;
      username: string;
      score: number;
      profilePhotoUrl?: string;
    }> = Object.entries<any>(data).map(([id, val]) => ({
      id,
      username: val.username,
      score: Number(val.score || 0),
      profilePhotoUrl: val.profilePhotoUrl,
    }));

    // Sort descending because limitToLast returns ascending value order
    const sorted = entries.sort((a, b) => b.score - a.score);

    const users: any[] = sorted.map((e) => ({
      id: e.id,
      username: e.username,
      weeklyScore: type === 'weekly' ? e.score : 0,
      monthlyScore: type === 'monthly' ? e.score : 0,
      score: type === 'allTime' ? e.score : 0,
      profilePhotoUrl: e.profilePhotoUrl || null,
    }));

    callback(users as unknown as User[]);
  });
};

// Read News RankingBoard
export const getNewsRankingBoard = (
  type: LeaderboardType,
  callback: (users: User[]) => void,
  limit = 10
) => {
  const targetRef =
    type === 'weekly'
      ? newsRankingBoardWeeklyRef
      : type === 'monthly'
      ? newsRankingBoardMonthlyRef
      : newsRankingBoardAllTimeRef;

  const leaderboardQuery = query(targetRef, orderByChild('score'), limitToLast(limit));

  return onValue(leaderboardQuery, (snapshot) => {
    const data = snapshot.val() || {};
    const entries = Object.entries<any>(data).map(([id, val]) => ({
      id,
      username: val.username,
      score: Number(val.score || 0),
      profilePhotoUrl: val.profilePhotoUrl,
    }));

    const sorted = entries.sort((a, b) => b.score - a.score);

    const users: any[] = sorted.map((e) => ({
      id: e.id,
      username: e.username,
      weeklyScore: type === 'weekly' ? e.score : 0,
      monthlyScore: type === 'monthly' ? e.score : 0,
      score: type === 'allTime' ? e.score : 0,
      profilePhotoUrl: e.profilePhotoUrl || null,
    }));

    callback(users as unknown as User[]);
  });
};

// Seed initial demo data if the database is empty
export const seedInitialData = async () => {
  const snapshot = await get(usersRef);
  if (snapshot.exists()) return; // Only seed if no data exists

  const now = new Date();
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 1 }).getTime();
  const startOfCurrentMonth = startOfMonth(now).getTime();

  const demoUsers = [
    {
      username: 'gaming_legend',
      profilePhotoUrl: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 9850,
      weeklyScore: 350,
      monthlyScore: 1200,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'pixel_master',
      profilePhotoUrl: 'https://images.pexels.com/photos/614810/pexels-photo-614810.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 8920,
      weeklyScore: 420,
      monthlyScore: 980,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'victory_queen',
      profilePhotoUrl: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 8750,
      weeklyScore: 280,
      monthlyScore: 1050,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'cyber_ninja',
      profilePhotoUrl: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 8100,
      weeklyScore: 310,
      monthlyScore: 890,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'neon_striker',
      profilePhotoUrl: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 7650,
      weeklyScore: 230,
      monthlyScore: 820,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'epic_player',
      profilePhotoUrl: 'https://images.pexels.com/photos/713520/pexels-photo-713520.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 7300,
      weeklyScore: 190,
      monthlyScore: 750,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'digital_warrior',
      profilePhotoUrl: 'https://images.pexels.com/photos/91227/pexels-photo-91227.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 6900,
      weeklyScore: 240,
      monthlyScore: 680,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'pixel_hunter',
      profilePhotoUrl: 'https://images.pexels.com/photos/874158/pexels-photo-874158.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 6550,
      weeklyScore: 180,
      monthlyScore: 620,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'tech_master',
      profilePhotoUrl: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 6300,
      weeklyScore: 150,
      monthlyScore: 560,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'game_explorer',
      profilePhotoUrl: 'https://images.pexels.com/photos/1130626/pexels-photo-1130626.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 5980,
      weeklyScore: 130,
      monthlyScore: 490,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'supreme_gamer',
      profilePhotoUrl: 'https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 5600,
      weeklyScore: 100,
      monthlyScore: 430,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    },
    {
      username: 'elite_player',
      profilePhotoUrl: 'https://images.pexels.com/photos/834863/pexels-photo-834863.jpeg?auto=compress&cs=tinysrgb&w=100',
      score: 5300,
      weeklyScore: 90,
      monthlyScore: 380,
      lastWeeklyReset: startOfCurrentWeek,
      lastMonthlyReset: startOfCurrentMonth,
    }
  ];

  const updates: Record<string, any> = {};
  const weeklyUpdates: Record<string, any> = {};
  const monthlyUpdates: Record<string, any> = {};
  const allTimeUpdates: Record<string, any> = {};
  
  demoUsers.forEach((user) => {
    const userId = crypto.randomUUID();
    updates[userId] = user;
    
    // Add to leaderboard nodes
    weeklyUpdates[userId] = {
      score: user.weeklyScore,
      username: user.username,
      profilePhotoUrl: user.profilePhotoUrl
    };
    
    monthlyUpdates[userId] = {
      score: user.monthlyScore,
      username: user.username,
      profilePhotoUrl: user.profilePhotoUrl
    };
    
    allTimeUpdates[userId] = {
      score: user.score,
      username: user.username,
      profilePhotoUrl: user.profilePhotoUrl
    };
  });

  // Update all nodes simultaneously
  await Promise.all([
    set(usersRef, updates),
    set(weeklyRef, weeklyUpdates),
    set(monthlyRef, monthlyUpdates),
    set(allTimeRef, allTimeUpdates)
  ]);
};
